import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

export interface FirmConfig {
  name: string;
  tagline: string;
  phone: string;
  fax: string;
  address: string;
  supportEmail: string;
  logoUrl: string;
  primaryColor: string;
  accentColor: string;
  norrisEnabled: boolean;
  norrisUrl: string;
  sabrinaEnabled: boolean;
  sabrinaUrl: string;
  portalDomain: string;
  appDomain: string;
  emailDomain: string;
  tasksCompanionEnabled: boolean;
  tasksCompanionOrigin: string;
  primaryAppOrigin: string;
}

const DEFAULTS: FirmConfig = {
  name: '',
  tagline: '',
  phone: '',
  fax: '',
  address: '',
  supportEmail: '',
  logoUrl: '/static/firm-logo.png',
  primaryColor: '#1e40af',
  accentColor: '#3b82f6',
  norrisEnabled: false,
  norrisUrl: '',
  sabrinaEnabled: false,
  sabrinaUrl: '',
  portalDomain: '',
  appDomain: '',
  emailDomain: '',
  tasksCompanionEnabled: false,
  tasksCompanionOrigin: '',
  primaryAppOrigin: '',
};

const FirmConfigContext = createContext<FirmConfig>(DEFAULTS);

// eslint-disable-next-line react-refresh/only-export-components
export function useFirmConfig(): FirmConfig {
  return useContext(FirmConfigContext);
}

export function FirmConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<FirmConfig>(DEFAULTS);

  useEffect(() => {
    fetch('/api/firm-config')
      .then((res) => {
        if (!res.ok) throw new Error(`firm-config ${res.status}`);
        return res.json();
      })
      .then((data: FirmConfig) => {
        setConfig(data);
        // Set CSS custom properties for theming
        document.documentElement.style.setProperty('--firm-primary', data.primaryColor);
        document.documentElement.style.setProperty('--firm-accent', data.accentColor);
      })
      .catch(() => {
        // Use defaults on failure — the app still works
      });
  }, []);

  return (
    <FirmConfigContext.Provider value={config}>
      {children}
    </FirmConfigContext.Provider>
  );
}
